import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Bell, Heart, ShoppingCart, User, Mic, MicOff, Camera, ChevronDown, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useLocation as useLocationCtx } from "@/contexts/LocationContext";
import { DYNTRA_LOGO_URL } from "@/lib/assets";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import NotificationsMenu, { useNotificationsUnread } from "@/components/layout/NotificationsMenu";
import { useVoiceSearch } from "@/hooks/useVoiceSearch";

export default function TopBar() {
  const [query, setQuery] = useState("");
  const [suggestions] = useState(["Composite resin", "NSK handpiece", "Orthodontic brackets", "Dental burs", "Autoclave"]);
  const [focused, setFocused] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifBtnRef = useRef(null);
  const nav = useNavigate();
  const { user, logout, setAuthOpen, setAuthMode } = useAuth();
  const { cart } = useCart();
  const { count: wishlistCount } = useWishlist();
  const unread = useNotificationsUnread();
  const { location, openLocation } = useLocationCtx();
  const voice = useVoiceSearch();
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    nav(`/shop?q=${encodeURIComponent(query.trim())}`);
    setFocused(false);
  };

  // Voice: when a transcript arrives, run the search automatically
  useEffect(() => {
    if (voice.transcript) {
      setQuery(voice.transcript);
      nav(`/shop?q=${encodeURIComponent(voice.transcript)}&mode=voice`);
      voice.reset();
      setFocused(false);
    }
  }, [voice.transcript, voice, nav]);

  useEffect(() => {
    if (voice.error) {
      const msg = String(voice.error).toLowerCase();
      if (msg.includes("not-allowed") || msg.includes("permission")) {
        toast.error("Microphone permission was denied");
      } else if (msg.includes("no-speech")) {
        toast.info("Didn't catch that — please try again");
      } else if (msg !== "aborted") {
        toast.error("Voice search failed. Please try again.");
      }
    }
  }, [voice.error]);

  const onVoiceClick = () => {
    if (!voice.supported) {
      toast.info("Voice search isn't supported in this browser");
      return;
    }
    if (voice.listening) voice.stop();
    else voice.start();
  };

  const onImagePick = () => {
    fileInputRef.current?.click();
  };

  const onImageSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    const url = URL.createObjectURL(file);
    setImagePreview({ url, name: file.name });
    toast.success("Searching by image…");
    nav(`/shop?mode=image&image=${encodeURIComponent(file.name)}`);
    // reset the input so re-selecting the same file re-triggers change
    e.target.value = "";
  };

  const clearImagePreview = () => {
    if (imagePreview?.url) URL.revokeObjectURL(imagePreview.url);
    setImagePreview(null);
  };

  return (
    <div className="dyntra-container flex items-center gap-4 py-3">
      {/* Logo */}
      <Link to="/" data-testid="header-logo" className="shrink-0">
        <img src={DYNTRA_LOGO_URL} alt="Dyntra Store" className="h-10 sm:h-11 w-auto" />
      </Link>

      {/* Location */}
      <button
        type="button"
        data-testid="header-location-btn"
        onClick={openLocation}
        className="hidden md:flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left hover:border-[#4B2AAD]/40 transition-all"
      >
        <MapPin className="h-4 w-4 text-[#4B2AAD]" />
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Deliver to</span>
          <span data-testid="header-location-label" className="text-sm font-semibold text-gray-900 max-w-[180px] truncate">
            {location.city}{location.pincode ? `, ${location.pincode}` : ""}
          </span>
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>

      {/* Search */}
      <div className="relative flex-1 max-w-2xl">
        <form onSubmit={submit} className="relative">
          <div className={`flex items-center rounded-xl border overflow-hidden focus-within:ring-2 focus-within:ring-[#4B2AAD]/20 transition-all ${voice.listening ? "border-[#F43F5E] bg-[#FEF2F4]" : "border-gray-200 bg-white focus-within:border-[#4B2AAD]"}`}>
            <input
              data-testid="header-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 120)}
              placeholder={voice.listening ? "Listening…" : "Search dental products, brands, categories..."}
              className="flex-1 px-4 py-2.5 text-sm outline-none bg-transparent"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onImageSelected}
              className="hidden"
              data-testid="header-image-input"
            />
            <button
              type="button"
              data-testid="header-image-btn"
              onClick={onImagePick}
              className="p-2 hover:bg-gray-50 transition-colors"
              title="Search by image"
              aria-label="Search by image"
            >
              <Camera className="h-4 w-4 text-gray-500" />
            </button>
            <button
              type="button"
              data-testid="header-voice-btn"
              onClick={onVoiceClick}
              className={`p-2 transition-colors ${voice.listening ? "bg-[#F43F5E]/10 text-[#F43F5E]" : "hover:bg-gray-50 text-gray-500"}`}
              title={voice.supported ? (voice.listening ? "Stop listening" : "Voice search") : "Voice search not supported"}
              aria-label="Voice search"
              aria-pressed={voice.listening}
            >
              {voice.listening ? <MicOff className="h-4 w-4" /> : <Mic className={`h-4 w-4 ${voice.supported ? "" : "opacity-50"}`} />}
            </button>
            <button
              type="submit"
              data-testid="header-search-btn"
              className="bg-[#4B2AAD] hover:bg-[#361D82] px-4 py-2.5 transition-colors"
              aria-label="Search"
            >
              <Search className="h-5 w-5 text-white" />
            </button>
          </div>
          {imagePreview && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white pl-2 pr-1 py-1 text-xs">
              <img src={imagePreview.url} alt="preview" className="h-8 w-8 rounded-md object-cover" />
              <span className="text-gray-700 max-w-[160px] truncate">Searching by image · {imagePreview.name}</span>
              <button
                type="button"
                onClick={clearImagePreview}
                className="h-6 w-6 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Clear image search"
              >
                <X className="h-3.5 w-3.5 text-gray-500" />
              </button>
            </div>
          )}
        </form>

        {focused && (
          <div className="absolute z-40 left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="px-4 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Recent Searches</div>
            {suggestions.slice(0, 3).map((s) => (
              <button
                key={s}
                data-testid={`search-suggestion-${s.replace(/\s+/g, "-").toLowerCase()}`}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-gray-50 text-left"
                onMouseDown={(e) => { e.preventDefault(); setQuery(s); nav(`/shop?q=${encodeURIComponent(s)}`); setFocused(false); }}
              >
                <Search className="h-3.5 w-3.5 text-gray-400" />
                {s}
              </button>
            ))}
            <div className="border-t border-gray-100 px-4 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Popular</div>
            {suggestions.slice(3).map((s) => (
              <button
                key={s}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-gray-50 text-left"
                onMouseDown={(e) => { e.preventDefault(); setQuery(s); nav(`/shop?q=${encodeURIComponent(s)}`); setFocused(false); }}
              >
                <Search className="h-3.5 w-3.5 text-gray-400" />
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1 md:gap-2 ml-auto">
        <div className="relative">
          <button
            ref={notifBtnRef}
            type="button"
            data-testid="header-alerts-btn"
            onClick={() => setNotifOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={notifOpen}
            aria-label="Alerts"
            title="Alerts"
            className="flex items-center justify-center px-2 py-1.5 rounded-lg hover:bg-gray-50 relative"
          >
            <div className="relative">
              <Bell className="h-5 w-5 text-gray-700" />
              {unread > 0 && (
                <span className="absolute -top-1.5 -right-2 bg-[#FFB020] text-[#111827] text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-bold">{unread}</span>
              )}
            </div>
          </button>
          <NotificationsMenu open={notifOpen} onOpenChange={setNotifOpen} triggerRef={notifBtnRef} />
        </div>
        <Link
          to="/wishlist"
          data-testid="header-wishlist-btn"
          aria-label="Wishlist"
          title="Wishlist"
          className="flex items-center justify-center px-2 py-1.5 rounded-lg hover:bg-gray-50 relative"
        >
          <div className="relative">
            <Heart className={`h-5 w-5 ${wishlistCount > 0 ? "text-[#F43F5E] fill-[#F43F5E]" : "text-gray-700"}`} />
            {wishlistCount > 0 && (
              <span data-testid="header-wishlist-count" className="absolute -top-1.5 -right-2 bg-[#F43F5E] text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-bold">{wishlistCount}</span>
            )}
          </div>
        </Link>
        <Link
          to="/cart"
          data-testid="header-cart-btn"
          aria-label="Cart"
          title="Cart"
          className="flex items-center justify-center px-2 py-1.5 rounded-lg hover:bg-gray-50"
        >
          <div className="relative">
            <ShoppingCart className="h-5 w-5 text-gray-700" />
            <span data-testid="header-cart-count" className="absolute -top-1.5 -right-2 bg-[#4B2AAD] text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-bold">{cart.count || 0}</span>
          </div>
        </Link>

        {/* Sign in / profile */}
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="header-profile-btn" className="flex flex-col items-center px-2 py-1.5 rounded-lg hover:bg-gray-50 focus:outline-none">
              <User className="h-5 w-5 text-gray-700" />
              <span className="text-[11px] mt-0.5 text-gray-600 hidden sm:block max-w-[70px] truncate">{user.name}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-2">
                <div className="text-sm font-semibold">{user.name}</div>
                <div className="text-xs text-gray-500">{user.email}</div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-account" onClick={() => nav("/account/profile")}>My Account</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/account/orders")}>My Orders</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-wishlist" onClick={() => nav("/account/wishlist")}>Wishlist</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-rewards" onClick={() => nav("/account/reward-zone")}>Rewards</DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-help" onClick={() => nav("/account/help")}>Help &amp; Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            data-testid="header-signin-btn"
            onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
            aria-label="Sign In"
            title="Sign In"
            className="flex items-center justify-center px-2 py-1.5 rounded-lg hover:bg-gray-50"
          >
            <User className="h-5 w-5 text-gray-700" />
          </button>
        )}
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, label, badge, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex flex-col items-center px-2 py-1.5 rounded-lg hover:bg-gray-50 relative">
      <div className="relative">
        <Icon className="h-5 w-5 text-gray-700" />
        {badge && (
          <span className="absolute -top-1.5 -right-2 bg-[#FFB020] text-[#111827] text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-bold">{badge}</span>
        )}
      </div>
      <span className="text-[11px] mt-0.5 text-gray-600 hidden sm:block">{label}</span>
    </button>
  );
}
